import React, { useState, useEffect } from 'react';
import { FaPlay } from 'react-icons/fa';
import { BUCKET_LABELS } from './bucketLabels';
import { fetchModelRegistry, getModelDisplayName } from '../../utils/models';
import ResearchRunStatus from './ResearchRunStatus';
import ResearchResultCard from './ResearchResultCard';
import ResearchResultDetail from './ResearchResultDetail';
import AgentIdentityCard from '../AgentIdentityCard';

/**
 * PortfolioRunResearchTab — research execution scoped to a portfolio.
 *
 * The active agent is inferred from portfolio.active_agent.
 * The active research config is fetched from GET /agents/{agentId}/research/config
 * and the is_active one is used for the run.
 *
 * This component handles: run trigger, polling, results list, result detail/apply.
 * Config management (create/edit/delete) lives in the agent detail page.
 */
export default function PortfolioRunResearchTab({
  portfolioId,
  portfolio,
  config,
  authToken,
  onTokensChange,
}) {
  const [activeConfig, setActiveConfig] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [results, setResults] = useState([]);
  const [loadingResults, setLoadingResults] = useState(true);
  const [currentTicketId, setCurrentTicketId] = useState(null);
  const [viewingResult, setViewingResult] = useState(null);
  const [showRunDialog, setShowRunDialog] = useState(false);
  const [runtimeDirective, setRuntimeDirective] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [runError, setRunError] = useState(null);
  const [modelRegistry, setModelRegistry] = useState([]);

  const apiBaseUrl = config?.apiBaseUrl || window.location.origin.replace('/web', '');
  const DIRECTIONS_MAX_LENGTH = 2000;

  const authHeaders = authToken
    ? { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' }
    : { 'Content-Type': 'application/json' };

  const agentId = portfolio?.active_agent?.id;

  useEffect(() => {
    fetchModelRegistry(apiBaseUrl).then(setModelRegistry);
  }, [apiBaseUrl]);

  useEffect(() => {
    if (agentId) {
      fetchActiveConfig();
    } else {
      setLoadingConfig(false);
    }
  }, [agentId]);

  useEffect(() => {
    fetchResults();
  }, [portfolioId]);

  const fetchActiveConfig = async () => {
    try {
      setLoadingConfig(true);
      const response = await fetch(
        `${apiBaseUrl}/agents/${agentId}/research/config`,
        { headers: authHeaders }
      );
      if (!response.ok) throw new Error('Failed to fetch research configs');
      const data = await response.json();
      setActiveConfig(data.find(c => c.is_active) || null);
    } catch (err) {
      console.error('Failed to fetch research config:', err);
    } finally {
      setLoadingConfig(false);
    }
  };

  const fetchResults = async () => {
    try {
      setLoadingResults(true);
      const response = await fetch(
        `${apiBaseUrl}/portfolio/${portfolioId}/research/results`,
        { headers: authHeaders }
      );
      if (!response.ok) throw new Error('Failed to fetch research results');
      const data = await response.json();
      setResults(data);
    } catch (err) {
      console.error('Failed to fetch research results:', err);
    } finally {
      setLoadingResults(false);
    }
  };

  const handleRunResearch = async () => {
    setRunError(null);
    setIsSubmitting(true);
    try {
      const response = await fetch(`${apiBaseUrl}/portfolio/${portfolioId}/research/run`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          research_config_id: activeConfig.id,
          runtime_directive: runtimeDirective || null,
        }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail?.message || errorData.detail || `HTTP ${response.status}`);
      }
      const data = await response.json();
      setCurrentTicketId(data.ticket_id);
      setShowRunDialog(false);
      setRuntimeDirective('');
    } catch (err) {
      console.error('Failed to start research run:', err);
      setRunError(err.message || 'Failed to start research run');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResearchCompleted = (result) => {
    setCurrentTicketId(null);
    fetchResults();
    setViewingResult(result);
  };

  const handleResultViewed = async (id) => {
    try {
      const response = await fetch(
        `${apiBaseUrl}/portfolio/${portfolioId}/research/results/${id}`,
        { headers: authHeaders }
      );
      if (!response.ok) throw new Error('Failed to fetch result details');
      setViewingResult(await response.json());
    } catch (err) {
      console.error('Failed to fetch result:', err);
    }
  };

  // ── Empty states ────────────────────────────────────────────────────────────

  if (!agentId) {
    return (
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-10 text-center">
        <svg className="w-12 h-12 mx-auto text-slate-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
        <h4 className="text-slate-300 font-medium mb-2">No Agent Assigned</h4>
        <p className="text-slate-400 text-sm">Assign an agent to this portfolio to enable research runs.</p>
      </div>
    );
  }

  if (loadingConfig) {
    return (
      <div className="flex items-center justify-center p-10">
        <svg className="animate-spin h-6 w-6 text-cyan-400" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>
    );
  }

  if (!activeConfig) {
    return (
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-10 text-center">
        <svg className="w-12 h-12 mx-auto text-slate-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
        <h4 className="text-slate-300 font-medium mb-2">No Active Research Config</h4>
        <p className="text-slate-400 text-sm">
          Create and activate a research config in the agent's Research Configs settings.
        </p>
      </div>
    );
  }

  // ── Main UI ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">

      {/* Agent identity — links to agent detail for config changes */}
      <AgentIdentityCard agent={portfolio?.active_agent} label="Research config managed by" />

      {/* Active config summary + Run button */}
      <section className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-900/40 border border-cyan-700/50 text-cyan-300">
              {BUCKET_LABELS[activeConfig.bucket] || activeConfig.bucket}
            </span>
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-900/40 border border-green-700/50 text-green-300">
              Active
            </span>
            <span className="px-2.5 py-0.5 bg-purple-900/40 border border-purple-700/50 text-purple-300 text-xs font-medium rounded-full">
              {activeConfig.model_name
                ? getModelDisplayName(modelRegistry, activeConfig.model_name)
                : 'System Default'}
            </span>
          </div>
          <button
            onClick={() => setShowRunDialog(true)}
            disabled={!!currentTicketId}
            className="flex items-center gap-2 px-3 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FaPlay className="w-3 h-3" />
            Run Research
          </button>
        </div>
        <div>
          <h4 className="text-xs font-medium text-slate-400 mb-1">Methodology:</h4>
          <p className="text-slate-400 text-sm leading-relaxed line-clamp-3">
            {activeConfig.methodology_prompt}
          </p>
        </div>
      </section>

      {/* In-flight poll status */}
      {currentTicketId && (
        <ResearchRunStatus
          portfolioId={portfolioId}
          apiBaseUrl={apiBaseUrl}
          authToken={authToken}
          ticketId={currentTicketId}
          onCompleted={handleResearchCompleted}
          onDismiss={() => setCurrentTicketId(null)}
        />
      )}

      {/* Research History */}
      <section>
        <h3 className="text-lg font-semibold text-white mb-4">Research History</h3>
        {loadingResults ? (
          <div className="flex items-center justify-center p-8 bg-slate-800/50 border border-slate-700 rounded-xl">
            <svg className="animate-spin h-6 w-6 text-cyan-400" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
        ) : results.length === 0 ? (
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 text-center">
            <p className="text-slate-400 text-sm">No research results yet. Run a research job to see results here.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {results.map((item) => (
              <ResearchResultCard
                key={item.id}
                item={item}
                portfolioId={portfolioId}
                apiBaseUrl={apiBaseUrl}
                authToken={authToken}
                onView={handleResultViewed}
                onDelete={fetchResults}
              />
            ))}
          </div>
        )}
      </section>

      {/* Result detail slide-over */}
      {viewingResult && (
        <ResearchResultDetail
          portfolioId={portfolioId}
          portfolioName={portfolio?.portfolio_name || portfolio?.name}
          apiBaseUrl={apiBaseUrl}
          authToken={authToken}
          result={viewingResult}
          onBack={() => setViewingResult(null)}
          onApplied={() => {
            setViewingResult(null);
            if (onTokensChange) onTokensChange();
          }}
        />
      )}

      {/* Run Research Dialog */}
      {showRunDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowRunDialog(false)} />
          <div className="relative bg-slate-800 border border-slate-700 rounded-xl p-6 w-full max-w-lg shadow-2xl">
            <h3 className="text-lg font-semibold text-white mb-1">Run Research</h3>
            <p className="text-slate-400 text-sm mb-4">
              Using config: <span className="text-cyan-300">{BUCKET_LABELS[activeConfig.bucket] || activeConfig.bucket}</span>
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Focus this run on... (optional)
              </label>
              <textarea
                value={runtimeDirective}
                onChange={(e) => setRuntimeDirective(e.target.value)}
                disabled={isSubmitting}
                placeholder="E.g., Pay special attention to tokens launching new mainnet upgrades this week."
                className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2.5 text-slate-200 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-colors font-mono text-sm resize-y"
                rows={4}
                maxLength={DIRECTIONS_MAX_LENGTH}
              />
              <div className="mt-1 text-xs text-slate-400">
                {runtimeDirective.length} / {DIRECTIONS_MAX_LENGTH} characters
              </div>
            </div>

            {runError && (
              <div className="mb-4 p-3 bg-red-900/30 border border-red-800 rounded-lg text-red-300 text-sm">
                {runError}
              </div>
            )}

            <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-700/50">
              <button
                onClick={() => { setShowRunDialog(false); setRunError(null); }}
                disabled={isSubmitting}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg font-medium transition-colors border border-slate-600"
              >
                Cancel
              </button>
              <button
                onClick={handleRunResearch}
                disabled={isSubmitting}
                className="flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Starting...
                  </>
                ) : (
                  <>
                    <FaPlay className="w-4 h-4" />
                    Start Research
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
